import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { PRIORITY, type ValueRenderer } from "../types";

const AMOUNT_KEY_RE = /(amount|balance|value)/i;

/** Formats a raw integer token amount string by its decimals. Pure string math
 *  so very large amounts never lose precision through Number. */
function formatRawAmount(raw: string, decimals: number): string {
  const negative = raw.startsWith("-");
  const digits = (negative ? raw.slice(1) : raw).padStart(decimals + 1, "0");
  const whole = digits.slice(0, digits.length - decimals);
  const frac = digits.slice(digits.length - decimals).replace(/0+$/, "");
  const grouped = whole.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  return `${negative ? "-" : ""}${grouped}${frac ? "." + frac : ""}`;
}

export const TokenAmountRenderer: ValueRenderer = {
  id: "token-amount",
  match: (value, ctx) => {
    if (typeof value !== "string" || !/^-?\d+$/.test(value)) return null;
    if (!ctx.key || !AMOUNT_KEY_RE.test(ctx.key)) return null;
    const parent = ctx.parent ?? {};
    const hasDecimals = typeof parent.decimals === "number";
    const hasSymbol = typeof parent.symbol === "string" || typeof parent.tokenSymbol === "string";
    if (!hasDecimals && !hasSymbol) return null;
    return { priority: PRIORITY.CONTEXT_HINTED, confidence: "high" };
  },
  Component: ({ value, ctx }) => {
    const raw = value as string;
    const parent = ctx.parent ?? {};
    const decimals = typeof parent.decimals === "number" ? (parent.decimals as number) : 18;
    const symbol =
      (typeof parent.symbol === "string" && parent.symbol) ||
      (typeof parent.tokenSymbol === "string" && parent.tokenSymbol) ||
      "";
    const formatted = formatRawAmount(raw, decimals);
    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <span className="tabular-nums">
              {formatted}
              {symbol ? <span className="text-ds-text-muted ml-1">{symbol}</span> : null}
            </span>
          </TooltipTrigger>
          <TooltipContent>
            {raw} (raw, {decimals} decimals)
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  },
};
